import {createStyles} from '@mantine/core';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useHoverState from 'ui-library/hooks/useHoverState';
import AddIcon from 'ui-library/icons/AddIcon';

const useStyles = createStyles((theme) => ({
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: 4,
    background: 'none',
    border: 'none',
    padding: 0,
  },
  enabledButton: {
    cursor: 'pointer',
  },
  disabledButton: {
    cursor: 'not-allowed',
  },
  icon: {
    color: theme.colors.blue[5],
    fontSize: 12,
  },
  disabledIcon: {
    color: theme.colors.gray[6],
  },
  hoveredEnabledIcon: {
    color: theme.colors.blue[5],
  },
}));

interface InlineAddViewButtonProps {
  onClick: () => void;
  disabledReason: string | null;
}

/**
 * A compact icon-only "+" button designed to be rendered inline within a
 * sidebar item's action button slot. Opens the Add View popover when clicked.
 *
 * @package
 */
export default function InlineAddViewButton({
  onClick,
  disabledReason,
}: InlineAddViewButtonProps): JSX.Element {
  const disabled = disabledReason != null;
  const [isHovered, hoverProps] = useHoverState();
  const {classes, cx} = useStyles();
  return (
    <Tooltip text={disabledReason ?? 'Add view'} isDisabled={false}>
      <button
        type="button"
        className={cx(classes.button, disabled ? classes.disabledButton : classes.enabledButton)}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        aria-label="Add view"
        {...hoverProps}
        {...getTestProps({
          component: 'InlineAddViewButton',
          element: 'root',
        })}
      >
        <AddIcon
          className={cx(
            classes.icon,
            isHovered && !disabled && classes.hoveredEnabledIcon,
            disabled && classes.disabledIcon
          )}
          disabled={disabled}
        />
      </button>
    </Tooltip>
  );
}
